import React, { useEffect, useMemo, useState } from 'react';
import {
    Text,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    TooltipHost,
    Pivot,
    PivotItem,
    ActionButton,
    useTheme,
} from '@fluentui/react';
import {
    CloudRegionUptime,
    HeartbeatWindowSummary,
    UptimeBucket,
} from '../../AGCP/models/EnvironmentModels';
import { getHeartbeatUptime } from '../../AGCP/api/AGCPApi';
import { getStatusColor } from '../utils/StatusHelpers';
import { useIsMobile } from '../../Common/hooks/useIsMobile';

type UptimeView = '1day' | '7day';

const formatDuration = (durationMs?: number | null): string => {
    if (durationMs === null || durationMs === undefined) {
        return 'n/a';
    }

    if (durationMs >= 60000) {
        const minutes = Math.floor(durationMs / 60000);
        const seconds = Math.round((durationMs % 60000) / 1000);
        return seconds === 60 ? `${minutes + 1}m` : `${minutes}m ${seconds}s`;
    }

    return durationMs < 1000
        ? `${Math.round(durationMs)} ms`
        : `${(durationMs / 1000).toFixed(1)} s`;
};

const formatAckAge = (lastAckUtc?: string | null): string => {
    if (!lastAckUtc) {
        return 'No ACK';
    }

    const ageMs = Date.now() - new Date(lastAckUtc).getTime();
    if (!Number.isFinite(ageMs) || ageMs < 0) {
        return 'Unavailable';
    }
    const ageMinutes = Math.floor(ageMs / 60000);

    if (ageMinutes < 1) {
        return '<1 min ago';
    }

    if (ageMinutes < 60) {
        return `${ageMinutes} min ago`;
    }

    const ageHours = Math.floor(ageMinutes / 60);
    return ageHours < 48
        ? `${ageHours} hr ago`
        : `${Math.floor(ageHours / 24)} days ago`;
};

/**
 * Formats a bucket's time span + sent/received counts for its hover tooltip.
 */
const formatBucketTooltip = (bucket: UptimeBucket): string => {
    const start = new Date(bucket.bucketStartUtc);
    const end = new Date(bucket.bucketEndUtc);
    const range = `${start.toLocaleString()} - ${end.toLocaleString()}`;

    if (bucket.sentCount === 0) {
        return `${range}\nNo data`;
    }

    const latency =
        bucket.averageDurationMs != null
            ? `\nAverage RTT: ${formatDuration(bucket.averageDurationMs)}`
            : '';

    const health =
        bucket.matureCount == null || bucket.onTimeCount == null
            ? 'On-time delivery unavailable'
            : `${bucket.onTimeCount}/${bucket.matureCount} mature sends on time (younger sends excluded)`;
    return `${range}\n${bucket.receivedCount}/${
        bucket.sentCount
    } actual receipts\n${health}\nLate: ${
        bucket.lateCount ?? 'Unavailable'
    }; deadline missed: ${
        bucket.deadlineMissedCount ?? 'Unavailable'
    } (includes late)${latency}`;
};

interface SummaryStatProps {
    label: string;
    value: string;
    tooltip: string;
}

const SummaryStat: React.FC<SummaryStatProps> = ({ label, value, tooltip }) => {
    const theme = useTheme();

    return (
        <TooltipHost content={tooltip}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    whiteSpace: 'nowrap',
                }}
            >
                <Text
                    variant="tiny"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            fontWeight: 600,
                        },
                    }}
                >
                    {label}
                </Text>
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: theme.palette.neutralPrimary,
                            fontWeight: 600,
                        },
                    }}
                >
                    {value}
                </Text>
            </div>
        </TooltipHost>
    );
};

const formatLatencyTooltip = (
    label: string,
    average?: number | null,
    median?: number | null,
    p95?: number | null
): string =>
    `${label}\nAverage ${formatDuration(average)}\nMedian ${formatDuration(
        median
    )}\np95 ${formatDuration(p95)}`;

interface HeartbeatDetailsProps {
    summary: HeartbeatWindowSummary;
    lastAckUtc?: string | null;
    lastOnTimeAckUtc?: string | null;
    deliveryDeadlineMinutes?: number;
}

const HeartbeatDetails: React.FC<HeartbeatDetailsProps> = ({
    summary,
    lastAckUtc,
    lastOnTimeAckUtc,
    deliveryDeadlineMinutes,
}) => {
    const theme = useTheme();
    const deliveryTooltip = `${summary.completedCount} actual receipts for ${summary.sentCount} sends in this window, including late arrivals and fast immature completions\n${summary.pendingCount} unacknowledged sends aged at least 15 minutes (diagnostic only)`;

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '8px 16px',
                marginTop: '9px',
                padding: '8px 10px',
                backgroundColor: theme.palette.neutralLighterAlt,
                borderLeft: `3px solid ${theme.palette.themePrimary}`,
                color: theme.palette.neutralPrimary,
            }}
        >
            <SummaryStat
                label="Average RTT"
                value={formatDuration(summary.averageDurationMs)}
                tooltip={formatLatencyTooltip(
                    'Round trip',
                    summary.averageDurationMs,
                    summary.medianDurationMs,
                    summary.p95DurationMs
                )}
            />
            <SummaryStat
                label="Ingress Average"
                value={formatDuration(summary.averageIngressDurationMs)}
                tooltip={formatLatencyTooltip(
                    'Ingress',
                    summary.averageIngressDurationMs,
                    summary.medianIngressDurationMs,
                    summary.p95IngressDurationMs
                )}
            />
            <SummaryStat
                label="Egress Average"
                value={formatDuration(summary.averageEgressDurationMs)}
                tooltip={formatLatencyTooltip(
                    'Egress',
                    summary.averageEgressDurationMs,
                    summary.medianEgressDurationMs,
                    summary.p95EgressDurationMs
                )}
            />
            <SummaryStat
                label={`Delivery · ${summary.pendingCount} Pending ≥15m`}
                value={`${summary.completedCount}/${summary.sentCount} ACKed`}
                tooltip={deliveryTooltip}
            />
            <SummaryStat
                label="On-time / Mature"
                value={
                    summary.onTimeCount == null || summary.matureCount == null
                        ? 'Unavailable'
                        : `${summary.onTimeCount}/${summary.matureCount}`
                }
                tooltip={`Fixed inclusive deadline: ${
                    deliveryDeadlineMinutes ?? 'Unavailable'
                } minutes. Sends younger than the deadline are excluded, even if already received. Observed p95 never changes the deadline.`}
            />
            <SummaryStat
                label="Late / Deadline Missed"
                value={`${summary.lateCount ?? 'Unavailable'} / ${
                    summary.deadlineMissedCount ?? 'Unavailable'
                }`}
                tooltip="Deadline missed includes both late arrivals and mature sends still missing a valid ACK. Late receipts retain latency statistics but never heal history or restore live health."
            />
            <SummaryStat
                label="Last Receipt"
                value={formatAckAge(lastAckUtc)}
                tooltip={
                    lastAckUtc
                        ? new Date(lastAckUtc).toLocaleString()
                        : 'No receipt observed'
                }
            />
            <SummaryStat
                label="Last On-time ACK"
                value={
                    deliveryDeadlineMinutes == null
                        ? 'Unavailable'
                        : formatAckAge(lastOnTimeAckUtc)
                }
                tooltip={
                    deliveryDeadlineMinutes == null
                        ? 'On-time ACK data unavailable from this backend'
                        : lastOnTimeAckUtc
                        ? `${new Date(
                              lastOnTimeAckUtc
                          ).toLocaleString()} — live health clock: <30m operational, 30–60m degraded, ≥60m down`
                        : 'No on-time ACK observed. Age the oldest observed send: startup unknown, 30m degraded, 60m down.'
                }
            />
        </div>
    );
};

const fourHourBlock = (date: Date): number => Math.floor(date.getHours() / 4);

/**
 * Label for the tick mark where this bucket crosses a boundary from the previous one — a calendar-day
 * change for the 7-day (4h-bucket) view, a 4-hour block change for the 1-day (30min-bucket) view. Compares
 * actual bucket timestamps rather than fixed bucket-index math so it stays correct regardless of bucket size.
 */
const getBoundaryLabel = (
    view: UptimeView,
    bucket: UptimeBucket,
    previousBucket: UptimeBucket | undefined
): string | null => {
    if (!previousBucket) {
        return null;
    }

    const start = new Date(bucket.bucketStartUtc);
    const previousStart = new Date(previousBucket.bucketStartUtc);

    if (view === '1day') {
        if (
            start.toDateString() === previousStart.toDateString() &&
            fourHourBlock(start) === fourHourBlock(previousStart)
        ) {
            return null;
        }

        return start.toLocaleTimeString([], { hour: 'numeric' });
    }

    if (start.toDateString() === previousStart.toDateString()) {
        return null;
    }

    return start.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

interface UptimeBarProps {
    buckets: UptimeBucket[];
    barWidth: number;
    view: UptimeView;
}

/**
 * A single Claude-status-page-style row of colored segments, one per time bucket, with tick marks +
 * labels at day boundaries (7-day view) or 4-hour boundaries (1-day view) so the bar is readable at a
 * glance instead of just being an undifferentiated strip.
 */
const UptimeBar: React.FC<UptimeBarProps> = ({ buckets, barWidth, view }) => {
    const theme = useTheme();

    return (
        <div>
            <div style={{ display: 'flex', gap: '2px' }}>
                {buckets.map((bucket, index) => {
                    const boundaryLabel = getBoundaryLabel(
                        view,
                        bucket,
                        buckets[index - 1]
                    );

                    return (
                        <TooltipHost
                            key={index}
                            content={formatBucketTooltip(bucket)}
                            styles={{ root: { flex: 1, minWidth: barWidth } }}
                        >
                            <div
                                style={{
                                    height: '24px',
                                    width: '100%',
                                    borderRadius: '2px',
                                    backgroundColor: getStatusColor(
                                        bucket.matureCount == null ||
                                            bucket.onTimeCount == null
                                            ? 'Unknown'
                                            : bucket.status
                                    ),
                                    opacity:
                                        bucket.matureCount == null ||
                                        bucket.onTimeCount == null ||
                                        bucket.status === 'Unknown'
                                            ? 0.35
                                            : 1,
                                    borderLeft: boundaryLabel
                                        ? `1px solid ${theme.palette.neutralTertiary}`
                                        : undefined,
                                }}
                            />
                        </TooltipHost>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: '2px', marginTop: '2px' }}>
                {buckets.map((bucket, index) => {
                    const boundaryLabel = getBoundaryLabel(
                        view,
                        bucket,
                        buckets[index - 1]
                    );

                    return (
                        <div
                            key={index}
                            style={{
                                flex: 1,
                                minWidth: barWidth,
                                borderLeft: boundaryLabel
                                    ? `1px solid ${theme.palette.neutralTertiary}`
                                    : undefined,
                            }}
                        >
                            {boundaryLabel && (
                                <Text
                                    variant="tiny"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                            paddingLeft: '2px',
                                        },
                                    }}
                                >
                                    {boundaryLabel}
                                </Text>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

interface CloudRegionColumnProps {
    cloudRegion: CloudRegionUptime;
    view: UptimeView;
}

/**
 * One region's (primary or secondary) uptime bar for the currently-selected view, meant to render
 * side-by-side with its sibling region under a shared cloud heading.
 */
const CloudRegionColumn: React.FC<CloudRegionColumnProps> = ({
    cloudRegion,
    view,
}) => {
    const theme = useTheme();
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const buckets =
        view === '1day' ? cloudRegion.buckets24h : cloudRegion.buckets7d;
    const uptimePercentage =
        view === '1day'
            ? cloudRegion.uptimePercentage24h
            : cloudRegion.uptimePercentage7d;
    const summary =
        view === '1day' ? cloudRegion.summary24h : cloudRegion.summary7d;
    const barWidth = view === '1day' ? 6 : 4;
    const healthAvailable = cloudRegion.deliveryDeadlineMinutes != null;
    const statusColor =
        cloudRegion.isEnabled && healthAvailable
            ? getStatusColor(cloudRegion.currentStatus)
            : theme.palette.neutralSecondary;

    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '4px',
                    marginBottom: '6px',
                }}
            >
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.neutralPrimary,
                            textTransform: 'capitalize',
                        },
                    }}
                >
                    {cloudRegion.regionMoniker}
                </Text>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '10px',
                    }}
                >
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralSecondary,
                                fontWeight: 600,
                            },
                        }}
                    >
                        {!healthAvailable ||
                        summary?.matureCount == null ||
                        summary?.onTimeCount == null
                            ? 'On-time delivery unavailable'
                            : summary.matureCount === 0
                            ? 'N/A On-time delivery'
                            : `${uptimePercentage.toFixed(
                                  2
                              )}% On-time delivery`}
                    </Text>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                            }}
                        />
                        <Text
                            variant="small"
                            styles={{
                                root: { color: statusColor, fontWeight: 600 },
                            }}
                        >
                            {!cloudRegion.isEnabled
                                ? 'Disabled'
                                : healthAvailable
                                ? cloudRegion.currentStatusLabel
                                : 'Health unavailable'}
                        </Text>
                    </div>
                </div>
            </div>

            <UptimeBar buckets={buckets} barWidth={barWidth} view={view} />
            {cloudRegion.isEnabled ? (
                <div style={{ marginTop: '2px' }}>
                    <ActionButton
                        iconProps={{
                            iconName: detailsExpanded
                                ? 'ChevronUp'
                                : 'ChevronDown',
                        }}
                        onClick={() => setDetailsExpanded(!detailsExpanded)}
                        aria-expanded={detailsExpanded}
                        styles={{
                            root: {
                                height: '28px',
                                padding: 0,
                            },
                            label: {
                                fontSize: theme.fonts.small.fontSize,
                                fontWeight: 600,
                            },
                        }}
                    >
                        {detailsExpanded ? 'Hide Details' : 'Show Details'}
                    </ActionButton>
                    {detailsExpanded && summary && (
                        <HeartbeatDetails
                            summary={summary}
                            lastAckUtc={cloudRegion.lastAckUtc}
                            lastOnTimeAckUtc={cloudRegion.lastOnTimeAckUtc}
                            deliveryDeadlineMinutes={
                                cloudRegion.deliveryDeadlineMinutes
                            }
                        />
                    )}
                </div>
            ) : (
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            display: 'block',
                            marginTop: '8px',
                        },
                    }}
                >
                    Messaging heartbeat is disabled for this cloud.
                </Text>
            )}
        </div>
    );
};

interface CloudGroupRowProps {
    cloudName: string;
    regions: CloudRegionUptime[];
    view: UptimeView;
}

/**
 * One cloud's heading with its regions (primary/secondary) stacked beneath it.
 */
const CloudGroupRow: React.FC<CloudGroupRowProps> = ({
    cloudName,
    regions,
    view,
}) => {
    const theme = useTheme();

    return (
        <div
            style={{
                padding: '16px',
                borderBottom: `1px solid ${theme.palette.neutralLighter}`,
            }}
        >
            <Text
                variant="mediumPlus"
                styles={{
                    root: {
                        fontWeight: 700,
                        display: 'block',
                        marginBottom: '10px',
                    },
                }}
            >
                {cloudName}
            </Text>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                {regions.map((cloudRegion) => (
                    <CloudRegionColumn
                        key={cloudRegion.regionMoniker}
                        cloudRegion={cloudRegion}
                        view={view}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * All-clouds-at-once overview of the SRMP messaging heartbeat round-trip uptime, styled after the
 * status.claude.com uptime bars. Deliberately independent of the per-cloud dropdown/detail flow
 * elsewhere on the Cloud Status page — every cloud+region pair renders at once, grouped by cloud with
 * primary/secondary side-by-side. Defaults to the 1-day view; 7-day is available behind the selector.
 * See .project/messaging-heartbeat.
 */
export const HeartbeatUptime: React.FC = () => {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const [cloudRegions, setCloudRegions] = useState<CloudRegionUptime[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<UptimeView>('1day');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const response = await getHeartbeatUptime();

                if (response.succeeded) {
                    setCloudRegions(response.data.clouds);
                    setError(null);
                } else {
                    setError(
                        response.message || 'Failed to load heartbeat uptime'
                    );
                }
            } catch (err) {
                setError('Failed to load heartbeat uptime');
                console.error('Error loading heartbeat uptime:', err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const groupedByCloud = useMemo(() => {
        const map = new Map<string, CloudRegionUptime[]>();

        cloudRegions.forEach((cloudRegion) => {
            const existing = map.get(cloudRegion.cloudName);

            if (existing) {
                existing.push(cloudRegion);
            } else {
                map.set(cloudRegion.cloudName, [cloudRegion]);
            }
        });

        return Array.from(map.entries()).map(([cloudName, regions]) => ({
            cloudName,
            regions,
        }));
    }, [cloudRegions]);

    return (
        <div
            style={{
                backgroundColor: theme.palette.white,
                borderRadius: '4px',
                boxShadow:
                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                overflow: 'hidden',
                marginBottom: '16px',
            }}
        >
            <div
                style={{
                    padding: isMobile ? '16px' : '16px 20px',
                    backgroundColor: theme.palette.neutralQuaternary,
                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                }}
            >
                <Text
                    variant="xLarge"
                    styles={{
                        root: { fontWeight: 700, color: theme.palette.black },
                    }}
                >
                    Messaging Heartbeat On-time Delivery
                </Text>
            </div>

            {!loading && !error && cloudRegions.length > 0 && (
                <div
                    style={{
                        padding: isMobile ? '4px 16px 0' : '4px 20px 0',
                        borderBottom: `1px solid ${theme.palette.neutralLighter}`,
                    }}
                >
                    <Pivot
                        selectedKey={view}
                        onLinkClick={(item) =>
                            setView(
                                (item?.props.itemKey as UptimeView) ?? '1day'
                            )
                        }
                    >
                        <PivotItem headerText="1 Day" itemKey="1day" />
                        <PivotItem headerText="7 Days" itemKey="7day" />
                    </Pivot>
                </div>
            )}

            {loading && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '60px 20px',
                    }}
                >
                    <Spinner
                        size={SpinnerSize.large}
                        label="Loading heartbeat uptime..."
                    />
                </div>
            )}

            {error && !loading && (
                <div style={{ padding: '20px' }}>
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                    >
                        {error}
                    </MessageBar>
                </div>
            )}

            {!loading && !error && cloudRegions.length === 0 && (
                <div style={{ padding: '20px' }}>
                    <MessageBar
                        messageBarType={MessageBarType.info}
                        isMultiline={false}
                    >
                        No heartbeat data available
                    </MessageBar>
                </div>
            )}

            {!loading &&
                !error &&
                groupedByCloud.map((group) => (
                    <CloudGroupRow
                        key={group.cloudName}
                        cloudName={group.cloudName}
                        regions={group.regions}
                        view={view}
                    />
                ))}
        </div>
    );
};
